import React, { useEffect, useMemo, useRef } from 'react'
import { has, isEmpty, isNull, isUndefined, partial } from 'lodash-es'
import { intl } from 'lang/intl'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import classnames from 'classnames'

import { TtIconButton, TtTooltip } from '@23/react'

import { useWebinarFormatQuery } from '../../../../hooks/useWebinarFormatQuery'
import useWebinarsQuery from '../../../../hooks/useWebinarsQuery'
import WebinarInfoSidebarSpeakerListWithChyronTrigger from '../../../../components/WebinarInfoSidebarComponent/WebinarInfoSidebarSpeakerListWithChyronTrigger'
import { WebinarInfoSidebarGuestSpeakerList } from '../../../../components/WebinarInfoSidebarComponent/WebinarInfoSidebarGuestSpeakerList'
import usePlaybackIntent from '../../../../../Webinar/hooks/usePlaybackIntent'
import usePlaybackStagedVideos from '../../../../../Webinar/hooks/usePlaybackStagedVideos'
import HandoutsFeature from '../../../HandoutsFeature'
import { OverlayCornerTagInputComponent } from '../../../../../Webinar/components'
import {
  ClipboardButtonComponent,
  UploadComponent,
  WebinarInfoSidebarComponent,
} from '../../../../components'
import { PollsFeature } from '../../..'
import { hex2rgba, isLightColor } from '../../../../utils'
import {
  QuestionsPane,
  WebinarChatSelectorComponent,
  WebinarPanelComponent,
  WebinarPresenterSidebarActionButtonsComponent,
  WebinarSidebarActionButtonsComponent,
  WebinarSidebarQuestionsPanelComponent,
  WebinarSidebarSecondaryPanelComponent,
} from '..'
import ButtonComponent from '../../../../components/ButtonComponent/ButtonComponent'
import useFeatures from '../../../../hooks/useFeatures'
import useSpeakersQuery from '../../../../hooks/useSpeakersQuery'
import { ChyronType } from '../../../../../firebase/model/chyron-model'
import {
  setChyron,
  useOverlayState,
} from '../../../../../firebase/hooks/useOverlays'
import { useWebinarColors } from '../../../../hooks/useWebinarColors'
import { useLiveId } from '../../../../../global/blocs/roomInfo'
import { setSelectedChat } from '../../../../../Chat/blocs/chatVisibility'
import { Chat } from '../../../../../Chat/components/Chat/Chat'
import { ActionCard } from '../../../../../ActionCards/components/ActionCard'
import { useLiveDataQuery } from '../../../../../global/blocs/useLiveData'
import { AudienceList } from '../../../../../Audience/components/AudienceList'

import { JoinToSpeakList } from './join-to-speak-list'
import SessionsDrawer from './SessionsDrawer/SessionsDrawer'
import DigitalEventCard from './DigitalEventCard'

import './WebinarSidebarComponent.scss'

const handoutsNamespace = 'webinar-sidebar-handouts'
const mainSidebarPollsNamespace = 'webinar-main-sidebar-polls'
const secondarySidebarPollsNamespace = 'webinar-secondary-sidebar-polls'

const deriveSecondaryTextColor = (styles) => {
  if (!has(styles, 'color')) {
    return {}
  }

  return {
    ...styles,
    color: hex2rgba(styles.color, 0.85),
  }
}

const getWebinarLinkRibbon = (link, role, textStyle) => (
  <WebinarPanelComponent className="webinar-page-ribbon">
    <FormattedMessage defaultMessage="Share this URL so people can register for the webinar" />
    <div className="link-block" style={deriveSecondaryTextColor(textStyle)}>
      <a className="webinar-link" href={link} target="_blank" rel="noreferrer">
        {link}
      </a>
      <ClipboardButtonComponent
        text={link}
        title={intl.formatMessage({
          defaultMessage: 'Copy the webinar audience URL to clipboard',
        })}
      />
    </div>
  </WebinarPanelComponent>
)

const renderAudiencePane = (
  options,
  textStyle,
  sidebarTextStyle,
  hasJoinToSpeakEnabled,
  chyronData,
  liveData
) => {
  const { role, liveId, webinarViewLink, isDraft } = options

  if (
    !liveData ||
    isNull(liveId) ||
    isUndefined(liveData.token) ||
    isNull(role)
  ) {
    return null
  }

  const date = liveData.live_date_epoch * 1000
  const locale = liveData.locale.replace('_', '-')
  const webinarInfoLabel =
    chyronData?.overlay?.type === ChyronType.WebinarInfo
      ? intl.formatMessage({
          defaultMessage: 'Hide Webinar Info Card',
        })
      : intl.formatMessage({
          defaultMessage: 'Show Webinar Info Card',
        })

  const speakersOverviewAriaLabel =
    chyronData?.overlay?.type === ChyronType.SpeakersOverview
      ? intl.formatMessage({
          defaultMessage: 'Hide speaker overview',
        })
      : intl.formatMessage({
          defaultMessage: 'Show speaker overview',
        })

  const speakers =
    chyronData?.speakers
      ?.filter((s) => s.creationSource !== 'guest' && !s.isHidden)
      .map((s) => ({
        name: s.name,
        title: s.title,
        avatarUrl: s.avatarURL,
        company: s.company,
      })) ?? []

  let webinarLinkRibbon = null

  if (!isDraft && role !== 'audience') {
    webinarLinkRibbon = getWebinarLinkRibbon(webinarViewLink, role, textStyle)
  }

  return (
    <div className="audience-panel">
      <section className="sidebar-info-webinar">
        <div className="sidebar-info-webinar-title">{liveData.name}</div>
        <div className="sidebar-info-webinar-date">
          <span>
            {new Intl.DateTimeFormat(locale, {
              dateStyle: 'full',
              timeStyle: 'short',
            }).format(new Date(date))}
          </span>
          {chyronData && (
            <TtTooltip distance={0} content={webinarInfoLabel}>
              <TtIconButton
                data-active={
                  chyronData.overlay?.type === ChyronType.WebinarInfo
                }
                className="sidebar-info-webinar-show-chyron-button"
                name="eye"
                library="23"
                label={webinarInfoLabel}
                onClick={() => {
                  if (chyronData.overlay?.type === ChyronType.WebinarInfo) {
                    setChyron(null)
                  } else {
                    setChyron({
                      type: ChyronType.WebinarInfo,
                      title: liveData.name,
                      thumbnail: liveData.thumbnail_small_download,
                      date,
                      locale,
                    })
                  }
                }}
              />
            </TtTooltip>
          )}
        </div>
        <p className="sidebar-info-webinar-description">
          {liveData.description_text}
        </p>
      </section>
      <div className="section">
        <div className="section-title">
          <FormattedMessage defaultMessage="Speakers" />
          {chyronData && speakers.length > 0 && (
            <TtTooltip
              distance={0}
              content={
                chyronData.overlay?.type === ChyronType.SpeakersOverview
                  ? intl.formatMessage({
                      defaultMessage: 'Hide Speakers Overview Card',
                    })
                  : intl.formatMessage({
                      defaultMessage: 'Show Speakers Overview Card',
                    })
              }
            >
              <TtIconButton
                label={speakersOverviewAriaLabel}
                name="eye"
                onClick={() => {
                  if (
                    chyronData.overlay?.type === ChyronType.SpeakersOverview
                  ) {
                    setChyron(null)
                  } else {
                    setChyron({
                      type: ChyronType.SpeakersOverview,
                      speakers,
                    })
                  }
                }}
                style={{
                  color:
                    chyronData.overlay?.type === ChyronType.SpeakersOverview
                      ? chyronData.accentColor
                      : 'inherit',
                  '--hover-color': chyronData.accentColor,
                  verticalAlign: 'middle',
                }}
              />
            </TtTooltip>
          )}
        </div>
        <WebinarPanelComponent>
          <WebinarInfoSidebarSpeakerListWithChyronTrigger />
        </WebinarPanelComponent>
      </div>
      {role === 'producer' &&
        hasJoinToSpeakEnabled &&
        liveData.streaming_method !== 'hardware' && (
          <>
            <WebinarInfoSidebarGuestSpeakerList
              liveId={liveId}
              token={liveData.token}
            />

            <JoinToSpeakList sessionId={liveId} />
          </>
        )}
      {role === 'producer' && (
        <div className="section audience-list-section">
          <div className="section-title no-padding">
            <FormattedMessage defaultMessage="Audience" />
          </div>
          {webinarLinkRibbon}
          <AudienceList />
        </div>
      )}
    </div>
  )
}

const renderChatPane = (
  textStyle,
  HasSpeakerChatEnabled,
  audienceChatEnabled
) => {
  if (audienceChatEnabled || HasSpeakerChatEnabled) {
    return (
      <div
        className="comments-wrapper comments-type-chat"
        style={deriveSecondaryTextColor(textStyle)}
      >
        <Chat />
      </div>
    )
  }

  return <WebinarInfoSidebarComponent />
}

const renderHandoutsPane = (options, colors) => {
  const { textStyle, accentColor, tabColor, iconColor } = colors

  return (
    <HandoutsFeature
      namespace={handoutsNamespace}
      liveId={options.liveId}
      liveToken={options.token}
      style={{ ...textStyle, backgroundColor: tabColor }}
      styles={{ progressBarColor: accentColor }}
      iconColor={iconColor}
      mode="view"
    />
  )
}

const renderCornerTagOverlayInput = (options) => {
  if (options.role !== 'producer' || options.isInGearMode) {
    return null
  }

  return <OverlayCornerTagInputComponent />
}

const getPollPanelTitleIcon = (isProducer, pollProps, colors) => {
  const { isPollActive, title } = pollProps
  const { iconColor, accentColor } = colors
  const icon = { ...pollProps.icon, color: iconColor }

  if (isEmpty(title)) {
    return {}
  }

  if (!isProducer) {
    return { title: intl.formatMessage({ defaultMessage: 'Poll' }), icon }
  }

  return {
    title,
    icon: isPollActive ? { ...icon, color: accentColor } : icon,
  }
}

// pollProps are coming from the PollFeature
const renderPollUnitWrapper = (isProducer, style, colors, pollProps) => {
  return (
    <WebinarPanelComponent
      style={style}
      {...pollProps}
      {...getPollPanelTitleIcon(isProducer, pollProps, colors)}
    />
  )
}

const getPollsProps = ({
  liveId,
  token,
  style,
  styles,
  colors,
  isProducer,
  isInGearMode,
}) => {
  const { iconColor, accentColor } = colors

  return {
    style,
    styles,
    iconColor,
    accentColor,
    objectId: liveId,
    objectToken: token,
    renderPollUnitWrapper: partial(
      renderPollUnitWrapper,
      isProducer,
      style,
      colors
    ),
    disableOverlayTrigger: isInGearMode,
  }
}

const renderSecondaryPanelContent = (
  opts,
  hasJoinToSpeakEnabled,
  speakers,
  overlay,
  setPlaybackPlayIntent,
  togglePlayback,
  unstagePlaybackVideo,
  stagePlaybackVideos,
  loadPlaybackVideos,
  webinarColors,
  onUploadHandoutsClick,
  onUploadProgress,
  onInvalidatePolls,
  liveData
) => {
  const { props, commonCommentsProps, colors, pollsProps } = opts
  const opt = { ...props, token: liveData?.token }
  const { textStyle, sidebarTextStyle } = colors
  const accentColor = webinarColors.accent.color

  const isProducer = opt.role === 'producer'
  const isQuestionsEnabled = liveData?.question_mode === 'ask'

  switch (opt.secondaryPanelContentType) {
    case 'questions':
      return (
        <WebinarSidebarQuestionsPanelComponent
          toggleQuestionsMode={opt.toggleQuestionsMode}
          isQuestionsEnabled={isQuestionsEnabled}
          isShowingPermissionToggle={isProducer}
        >
          {isQuestionsEnabled && (
            <QuestionsPane
              unreadQuestionsIdList={opt.unreadQuestionsIdList}
              commonCommentsProps={commonCommentsProps}
            />
          )}
        </WebinarSidebarQuestionsPanelComponent>
      )
    case 'people': {
      const chyronData = isProducer
        ? {
            speakers,
            accentColor,
            overlay,
          }
        : undefined

      return renderAudiencePane(
        opt,
        textStyle,
        sidebarTextStyle,
        hasJoinToSpeakEnabled,
        chyronData,
        liveData
      )
    }
    case 'polls':
      return (
        <div className="polls-deprecation-warning">
          <FormattedMessage defaultMessage="Polls have been removed from the sidebar. You can now manage polls from the WebinarTray." />
        </div>
      )
    case 'handouts':
      return (
        <>
          {renderHandoutsPane(opt, colors)}
          <UploadComponent
            showUploadList={false}
            customRequest={(...o) =>
              onUploadHandoutsClick(handoutsNamespace, o)
            }
            onChange={({ event, file }) => {
              if (event) {
                onUploadProgress(file.name, event.percent)
              }
            }}
          >
            <ButtonComponent
              style={{
                backgroundColor: webinarColors.accent.color,
                color: webinarColors.accent.contrast.color,
                width: '100%',
              }}
              color="transparent"
              className="action-button"
              ariaLabel={intl.formatMessage({
                defaultMessage: 'Handouts',
              })}
              round
            >
              <FormattedMessage defaultMessage="Upload" />
            </ButtonComponent>
          </UploadComponent>
        </>
      )
    case 'sessions':
      return (
        <SessionsDrawer
          seriesId={Number(liveData?.live_series_id)}
          seriesToken={liveData?.live_series_token}
          webinarId={Number(opt.liveId)}
        />
      )
    default:
      return null
  }
}

const getUnreadQuestionsCount = (props) => {
  const {
    unreadQuestionsIdList,
    isSecondaryPanelVisible,
    secondaryPanelContentType,
  } = props

  if (isSecondaryPanelVisible && secondaryPanelContentType === 'questions') {
    return 0
  }

  return unreadQuestionsIdList.size
}

const WebinarSidebarComponent = (props) => {
  const {
    role,
    presenterToken,
    uuid,
    isSecondaryPanelVisible,
    toggleSecondaryPanel,
    secondaryPanelContentType,
    onUploadHandoutsClick,
    onUploadProgress,
    togglePollOverlay,
    isInGearMode,
    onInvalidatePolls,
  } = props
  const mainPanelRef = useRef(null)
  const liveId = useLiveId()

  const { data: liveData } = useLiveDataQuery()

  const isQuestionsEnabled = liveData?.question_mode === 'ask'

  const audienceChatEnabled = useMemo(() => {
    return (
      liveData?.comment_mode === 'all' || liveData?.comment_mode === 'speakers'
    )
  }, [liveData])

  const {
    HasJoinToSpeakEnabled,
    HasWebinarActionCardsEnabled,
    HasSpeakerChatEnabled,
  } = useFeatures()

  useEffect(() => {
    if (!isSecondaryPanelVisible) {
      if (secondaryPanelContentType !== 'playback') {
        const actionButton = mainPanelRef.current.querySelector(
          `button.${secondaryPanelContentType}`
        )
        if (actionButton !== null) actionButton.focus()
      }
    }
  }, [isSecondaryPanelVisible, secondaryPanelContentType])

  const { data: speakers = [] } = useSpeakersQuery()

  const { data: series } = useWebinarFormatQuery(
    liveData?.live_series_id,
    liveData?.live_series_token
  )

  const { data: webinars } = useWebinarsQuery({
    liveSeriesId: liveData?.live_series_id,
    liveStatus: 'live',
  })

  const isProducer = role === 'producer'
  const isPresenter = role === 'presenter'
  const isGuest =
    isPresenter &&
    speakers?.find((speaker) => presenterToken.endsWith(speaker.id))
      ?.creationSource === 'guest'
  const webinarColors = useWebinarColors()
  const textStyle = { color: webinarColors.tabs.contrast.color }
  const sidebarTextStyle = { color: webinarColors.sidebar.contrast.color }
  const backgroundColor = webinarColors.sidebar.color
  const accentColor = webinarColors.accent.color
  const tabColor = webinarColors.tabs.color

  const inputBackgroundColor = `rgba(0, 0, 0, ${
    isLightColor(backgroundColor) ? 0.05 : 0.15
  })`

  const iconColor = isLightColor(backgroundColor)
    ? 'rgba(0, 0, 0, 0.25)'
    : 'rgba(255, 255, 255, 0.25)'

  const colors = {
    textStyle,
    sidebarTextStyle,
    accentColor,
    tabColor,
    iconColor,
    backgroundColor,
  }

  const overlay = useOverlayState()

  useEffect(() => {
    if (!audienceChatEnabled && HasSpeakerChatEnabled) {
      setSelectedChat('speaker-chat')
    }
  }, [audienceChatEnabled, HasSpeakerChatEnabled])

  const isEitherChatEnabled = HasSpeakerChatEnabled || audienceChatEnabled

  const style = { ...textStyle, backgroundColor: tabColor }
  const styles = {
    inputStyle: { ...textStyle, backgroundColor: inputBackgroundColor },
    buttonStyle: { backgroundColor: accentColor },
    secondaryButtonStyle: {
      backgroundColor: `rgba(255, 255, 255, ${
        isLightColor(backgroundColor) ? 0.4 : 0.05
      })`,
      color: accentColor,
    },
    progressBarStyles: {
      strokeColor: accentColor,
      trailColor: webinarColors.sidebar.color,
    },
    radioButtonStyles: {
      passiveColor: webinarColors.tabs.contrast.color,
      activeColor: accentColor,
    },
  }

  const commonCommentsProps = {
    liveId,
    uuid,
    style,
    styles,
    renderListOnTop: true,
    isAutoResizeTextarea: true,
    canDelete: isProducer,
    token: liveData?.token,
  }

  const pollsProps = getPollsProps({
    liveId,
    token: liveData?.token,
    style,
    styles,
    colors,
    isProducer,
    isInGearMode,
  })

  if (role === 'presenter' && presenterToken) {
    commonCommentsProps.presenterToken = presenterToken
  }

  const mainPanelClasses = classnames('sidebar-main-panel-wrapper', {
    hidden: isSecondaryPanelVisible,
  })

  const { setPlaybackPlayIntent, togglePlayback } = usePlaybackIntent()
  const { unstagePlaybackVideo, stagePlaybackVideos, loadPlaybackVideos } =
    usePlaybackStagedVideos()

  return (
    <div
      className="webinar-sidebar-component"
      style={{
        '--webinar-color-accent': webinarColors.accent.color,
        '--webinar-color-sidebar': webinarColors.sidebar.color,
        '--webinar-color-tabs': webinarColors.tabs.color,
        '--webinar-color-sidebar-text':
          webinarColors.sidebar.highContrast.color,
        '--webinar-color-sidebar-ctacontrast':
          webinarColors.sidebar.contrast.color,
        '--webinar-color-tabs-text': webinarColors.tabs.highContrast.color,
        '--webinar-color-tabs-ctacontrast': webinarColors.tabs.contrast.color,
        '--webinar-color-accent-text': webinarColors.accent.highContrast.color,
        '--webinar-color-accent-ctacontrast':
          webinarColors.accent.contrast.color,
        '--webinar-color-inputBox':
          webinarColors.sidebar.contrast.color === '#000000'
            ? 'rgba(0, 0, 0, 0.05)'
            : 'rgba(0, 0, 0, 0.15)',
      }}
    >
      <div className="sidebar-header">
        {isProducer && (
          <WebinarSidebarActionButtonsComponent
            accentColor={accentColor}
            onActivityClick={() =>
              toggleSecondaryPanel(secondaryPanelContentType)
            }
            isAssociatedWebinar={
              liveData.live_series_id !== '' &&
              series?.seriesFormat !== 'series-recurring'
            }
            onWebinarsClick={() => toggleSecondaryPanel('sessions')}
            onPollsClick={() => toggleSecondaryPanel('polls')}
            onQuestionsClick={() => toggleSecondaryPanel('questions')}
            onHandoutsClick={() => toggleSecondaryPanel('handouts')}
            onPeopleClick={() => toggleSecondaryPanel('people')}
            unreadQuestionsCount={getUnreadQuestionsCount(props)}
            currentlySelectedSecondaryPanel={secondaryPanelContentType}
          />
        )}
        {isPresenter && !isGuest && isQuestionsEnabled && (
          <WebinarPresenterSidebarActionButtonsComponent
            accentColor={accentColor}
            onActivityClick={() =>
              toggleSecondaryPanel(secondaryPanelContentType)
            }
            onQuestionsClick={() => toggleSecondaryPanel('questions')}
            unreadQuestionsCount={getUnreadQuestionsCount(props)}
            currentlySelectedSecondaryPanel={secondaryPanelContentType}
          />
        )}
      </div>
      <div
        className={mainPanelClasses}
        style={{
          backgroundColor: webinarColors.sidebar.color,
          color: webinarColors.tabs.highContrast.color,
        }}
        ref={mainPanelRef}
      >
        {series &&
          series.sessionCount > 0 &&
          series.seriesFormat !== 'series-recurring' && (
            <DigitalEventCard
              title={series.name}
              thumbnailUrl={series.thumbnailUrl}
              numberOfSessions={series.sessionCount}
              onClick={() => toggleSecondaryPanel('sessions')}
            />
          )}
        {renderCornerTagOverlayInput(props)}
        {HasWebinarActionCardsEnabled && <ActionCard />}
        {!isPresenter && (
          <PollsFeature
            {...pollsProps}
            isPublicPollOnly
            canChangePoll={isProducer}
            isVotingAllowed={!isProducer}
            namespace={mainSidebarPollsNamespace}
            siblingFeatureNameSpace={secondarySidebarPollsNamespace}
            togglePollOverlay={togglePollOverlay}
            onInvalidatePolls={onInvalidatePolls}
          />
        )}
        {!isGuest && isEitherChatEnabled && <WebinarChatSelectorComponent />}
        {renderChatPane(textStyle, HasSpeakerChatEnabled, audienceChatEnabled)}
      </div>
      {isSecondaryPanelVisible && !isGuest && (
        <div className="secondary-panel-wrapper" style={{ backgroundColor }}>
          <WebinarSidebarSecondaryPanelComponent>
            {renderSecondaryPanelContent(
              {
                props,
                commonCommentsProps,
                colors,
                pollsProps,
              },
              HasJoinToSpeakEnabled,
              speakers,
              overlay,
              setPlaybackPlayIntent,
              togglePlayback,
              unstagePlaybackVideo,
              stagePlaybackVideos,
              loadPlaybackVideos,
              webinarColors,
              onUploadHandoutsClick,
              onUploadProgress,
              onInvalidatePolls,
              liveData
            )}
          </WebinarSidebarSecondaryPanelComponent>
        </div>
      )}
    </div>
  )
}

WebinarSidebarComponent.propTypes = {
  uuid: PropTypes.string,
  role: PropTypes.oneOf(['producer', 'presenter', 'audience']),
  presenterToken: PropTypes.string,
  webinarViewLink: PropTypes.string,
  isDraft: PropTypes.bool,
  peopleTabName: PropTypes.string,
  toggleQuestionOverlay: PropTypes.func,
  questionOverlayCommentId: PropTypes.string,
  isSecondaryPanelVisible: PropTypes.bool,
  toggleSecondaryPanel: PropTypes.func,
  secondaryPanelContentType: PropTypes.oneOf([
    'polls',
    'questions',
    'handouts',
    'people',
    'sessions',
    'playback',
  ]),
  toggleQuestionsMode: PropTypes.func,
  onUploadHandoutsClick: PropTypes.func,
  onUploadProgress: PropTypes.func,
  onAskQuestion: PropTypes.func,
  unreadQuestionsIdList: PropTypes.object,
  togglePollOverlay: PropTypes.func,
  isInGearMode: PropTypes.bool,
  onMicrophoneClick: PropTypes.func,
  onInvalidatePolls: PropTypes.func,
}

WebinarSidebarComponent.defaultProps = {
  onMicrophoneClick: () => {},
}

// Pretend to be ant's Sider
// TODO (noahlaux): this is fishy!
/* eslint-disable-next-line no-underscore-dangle */
WebinarSidebarComponent.__ANT_LAYOUT_SIDER = true

export default WebinarSidebarComponent
